import { Link } from "react-router-dom";

import { isOpenFeedDrop } from "../../services/dropsFeedService";
import { dropUnlockCtaLabel } from "../../utils/dropPricing";
import ProfileAvatarLink from "../profile/ProfileAvatarLink";
import ProfileNameLink from "../profile/ProfileNameLink";
import { getNicheMeta, NicheIcon } from "../niche";
import { formatCount } from "../../utils/formatCount";
import "./FeaturedCard.css";

function viewsLeft(drop) {
  return Math.max((drop.maxViews || 100) - (drop.viewCount || 0), 0);
}

function pctViewed(drop) {
  const max = drop.maxViews || 100;
  return Math.min(100, Math.round(((drop.viewCount || 0) / max) * 100));
}

function contentTypeLabel(drop) {
  const map = { video: "Video", photo: "Photo", audio: "Audio", article: "Article" };
  return map[drop.contentType] || "Drop";
}

function heroImageUrl(url) {
  const fallback =
    "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1600&q=90";
  if (!url) return fallback;
  if (url.includes("images.unsplash.com")) {
    try {
      const parsed = new URL(url);
      parsed.searchParams.set("q", "90");
      parsed.searchParams.set("fit", "crop");
      parsed.searchParams.set("w", "1600");
      return parsed.toString();
    } catch {
      return url;
    }
  }
  return url;
}

function thumbnailUrl(url) {
  const fallback =
    "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?q=85&fit=crop&w=900";
  if (!url) return fallback;
  if (url.includes("images.unsplash.com")) {
    try {
      const parsed = new URL(url);
      parsed.searchParams.set("q", "85");
      parsed.searchParams.set("fit", "crop");
      parsed.searchParams.set("w", "900");
      return parsed.toString();
    } catch {
      return url;
    }
  }
  return url;
}

function heroHeadline(creator, drop) {
  const parts = (drop?.title || creator?.displayName || "Discover").split(/[.—–-]/);
  const l1 = parts[0]?.trim() || "Exclusive drops.";
  const l2 = parts[1]?.trim() || drop?.subtitle?.replace(/\.$/, "") || "Real creators.";
  const l3 = drop?.subtitle?.replace(/\.$/, "") || parts[2]?.trim() || "No algorithm.";
  return { l1, l2, l3 };
}

/**
 * Shared editorial featured hero used by Gallery and Home.
 * `variant="gallery"` — platform featured, Follow + View CTAs, "See all featured" link
 * `variant="home"` — followed creator featured, Unlock + Preview CTAs
 */
export default function FeaturedCard({
  creator,
  drop,
  access = { type: "free" },
  followingIds = [],
  visible = true,
  variant = "gallery",
}) {
  const left = viewsLeft(drop);
  const pct = pctViewed(drop);
  const urgent = isOpenFeedDrop(drop) && !drop._synthetic && left <= 15;
  const meta = getNicheMeta(drop.niche || creator.niche);
  const handle = creator.handle || creator.username || creator.id;
  const isFollowing = followingIds.includes(creator.id);
  const { l1, l2, l3 } = heroHeadline(creator, drop);
  const dropCount = creator.totalDrops || creator.dropCount || 1;
  const subs = creator.followerCount || creator.supporterCount || 0;
  const dropPath = drop._synthetic ? `/creator/${handle}` : `/drop/${drop.id}`;
  const isHome = variant === "home";

  const unlocked =
    access.type === "free" ||
    access.type === "paid-unlocked" ||
    access.type === "sub-unlocked";
  const unlockLabel = drop._synthetic
    ? "See profile →"
    : dropUnlockCtaLabel(drop, {
        closed: access.type === "closed",
        unlocked,
      });

  const primaryCta = isHome
    ? { to: dropPath, label: unlockLabel || "Unlock", className: "gal-btn-follow" }
    : {
        to: `/creator/${handle}`,
        label: isFollowing ? "Following ✓" : `Follow ${creator.displayName?.split(" ")[0]} →`,
        className: "gal-btn-follow",
      };

  const secondaryCta = isHome
    ? { to: dropPath, label: "Preview", className: "gal-btn-view" }
    : {
        to: dropPath,
        label: drop._synthetic ? "See their drops" : "View Latest Drop",
        className: "gal-btn-view",
      };

  return (
    <section
      className={`gal-hero${visible ? "" : " gal-hero--fade-out"}`}
      aria-label={isHome ? "Featured from your creators" : "Featured today"}
    >
      <div
        className="gal-hero-bg"
        style={{ backgroundImage: `url('${heroImageUrl(drop.thumbnailUrl)}')` }}
      />
      <div className="gal-hero-vignette" />

      <div className="gal-random-badge">
        <span className="gal-rb-icon">✦</span>
        <span className="gal-rb-text">
          {isHome ? "Featured from your creators" : "Featured Today"}
        </span>
      </div>

      {!isHome && (
        <Link to="/featured" className="gal-featured-link">
          See all featured →
        </Link>
      )}

      <div className="gal-hero-content">
        <div className="gal-hc-left">
          <div className="gal-hc-creator">
            <ProfileAvatarLink
              user={creator}
              src={creator.avatarUrl || creator.avatar}
              alt={creator.displayName}
              className="gal-hc-av-link"
              imgClassName="gal-hc-av"
            />
            <ProfileNameLink user={creator} className="gal-hc-name">
              {creator.displayName}
            </ProfileNameLink>
            <span className="gal-hc-niche">
              <NicheIcon niche={meta.key} size={11} />
              {meta.label}
            </span>
            {creator.city && <span className="gal-hc-loc">{creator.city}</span>}
          </div>

          <h1 className="gal-hero-title">
            {l1}
            <br />
            {l2}
            <br />
            <em>{l3}</em>
          </h1>

          {creator.bio && <p className="gal-hero-bio">{creator.bio}</p>}

          <div className="gal-hero-stats">
            <div className="gal-hs-stat">
              <div className="gal-hss-num">{formatCount(subs)}</div>
              <div className="gal-hss-lbl">subscribers</div>
            </div>
            <div className="gal-hs-stat">
              <div className="gal-hss-num">{dropCount}</div>
              <div className="gal-hss-lbl">drops total</div>
            </div>
            <div className="gal-hs-stat">
              <div className="gal-hss-num">
                {drop.viewCount || 0}/{drop.maxViews || 100}
              </div>
              <div className="gal-hss-lbl">latest · {urgent ? "closing" : "open"}</div>
            </div>
          </div>

          <div className="gal-hero-actions">
            <Link to={primaryCta.to} className={primaryCta.className}>
              {primaryCta.label}
            </Link>
            <Link to={secondaryCta.to} className={secondaryCta.className}>
              {secondaryCta.label}
            </Link>
          </div>
        </div>

        <div className="gal-hc-right">
          <div className="gal-drop-widget">
            <div className={`gal-dw-window ${urgent ? "urgent" : "open"}`}>
              <span className={`gal-dw-text ${urgent ? "gal-dwt-r" : "gal-dwt-g"}`}>
                {urgent ? "⚡ Closing fast" : "Open · views left"}
              </span>
              <span className="gal-dw-timer" style={urgent ? { color: "var(--red)" } : undefined}>
                {drop.viewCount || 0}/{drop.maxViews || 100} taken
              </span>
            </div>
            <div className="gal-dw-img">
              <img src={thumbnailUrl(drop.thumbnailUrl)} alt="" />
              <div className="gal-dw-shade" />
              <div className="gal-dw-chip">
                <div className="gal-dwc-num">{drop.viewCount || 0}</div>
                <div className="gal-dwc-text">
                  <strong>of {drop.maxViews || 100}</strong>
                  {urgent ? "closing ⚡" : `${left} remain`}
                </div>
              </div>
            </div>
            <div className="gal-dw-scar-bar">
              <div className="gal-dwsb-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="gal-dw-body">
              <div className="gal-dw-type">{contentTypeLabel(drop)}</div>
              <div className="gal-dw-title">{drop.title}</div>
              <div className="gal-dw-foot">
                <div className="gal-dw-pill">
                  <span className="gal-dwp-dot" />
                  {drop.viewCount || 0}/{drop.maxViews || 100}
                  {urgent ? " ⚡" : ""}
                </div>
                <Link to={dropPath} className="gal-dw-unlock">
                  {unlockLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="gal-hero-fade" />
    </section>
  );
}
